import InterfaceIcon from "components/Icons/InterfaceIcon";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { events as allEvents, eventPath, eventPrice, eventState, eventLabels, isClosed } from "data/events";
import useBookingClock from "utils/useBookingClock";
import "../Styling/App.css";

const STORAGE_KEY = "elevate_event_popup_dismissed_v1";
function EventAnnouncementPopup() {
  const now = useBookingClock();
  const location = useLocation();
  const dialogRef = React.useRef(null);
  const events = allEvents.filter((event) => event.showInAnnouncement !== false && !isClosed(eventState(event, now)));
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    let dismissed = false;
    try { dismissed = window.localStorage.getItem(STORAGE_KEY) === "true"; } catch {}
    if (dismissed || location.pathname === "/book-now" || ["/Success", "/Error", "/Cancel"].includes(location.pathname)) {
      return undefined;
    }

    const timer = window.setTimeout(() => setIsVisible(true), 1200);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const handleDismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "true"); } catch {}
    setIsVisible(false);
  };

  React.useEffect(() => {
    if (!isVisible || !events.length) {
      return undefined;
    }

    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Tab") {
        const items = dialogRef.current?.querySelectorAll('button, a[href]');
        if (!items?.length) return;
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
      if (event.key === "Escape") {
        handleDismiss();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isVisible, events.length]);

  if (!events.length) {
    return null;
  }

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      handleDismiss();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="event-popup-backdrop"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <aside
        ref={dialogRef}
        className="event-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-popup-title"
      >
        <button
          type="button"
          className="event-popup-close"
          aria-label="Close masterclass announcement"
          onClick={handleDismiss}
        >
          <InterfaceIcon name="close" size="1em" />
        </button>

        <p className="event-popup-eyebrow">Masterclass updates</p>
        <h2 id="event-popup-title" className="event-popup-title">
          Upcoming Masterclasses
        </h2>
        <p className="event-popup-copy">
          View availability, register interest or join a waiting list. A seat is only confirmed through an approved booking.
        </p>

        <div className="event-popup-list">
          {events.map((event) => (
            <article className="event-popup-card" key={event.title}>
              <span className="event-popup-chip">{event.location}</span>
              <h3>{event.title}</h3>
              <p>{event.dateLabel}</p>
              <p className="event-popup-price">{eventPrice(event)}</p>
              <p>{eventLabels[eventState(event, now)]}</p>
              <div className="event-popup-actions">
                <Link
                  to={eventPath(event)}
                  onClick={handleDismiss}
                  className="event-popup-link"
                >
                  View event and next step
                </Link>
              </div>
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}

export default EventAnnouncementPopup;
